export interface SignVideo {
    id: string;
    start?: number;
    end?: number;
}

const ID_RE = /^[A-Za-z0-9_-]{11}$/;

const HOST_PATTERNS = [
    /(?:youtube\.com|youtube-nocookie\.com)\/(?:embed|shorts|live|v)\/([A-Za-z0-9_-]{11})/i,
    /youtu\.be\/([A-Za-z0-9_-]{11})/i,
    /(?:youtube\.com|youtube-nocookie\.com)\/watch\?(?:[^#]*&)?v=([A-Za-z0-9_-]{11})/i,
];

function parseTimestamp(raw: string | null): number | undefined {
    if (!raw) return undefined;
    const value = raw.trim().toLowerCase();
    if (!value) return undefined;

    if (/^\d+$/.test(value)) {
        const seconds = Number.parseInt(value, 10);
        return Number.isFinite(seconds) ? seconds : undefined;
    }

    const match = value.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s?)?$/);
    if (!match) return undefined;
    const [, h, m, s] = match;
    if (!h && !m && !s) return undefined;
    const seconds =
        Number.parseInt(h ?? "0", 10) * 3600 +
        Number.parseInt(m ?? "0", 10) * 60 +
        Number.parseInt(s ?? "0", 10);
    return Number.isFinite(seconds) ? seconds : undefined;
}

export function parseYouTubeRef(raw: string | undefined | null): SignVideo | null {
    if (!raw) return null;
    const value = raw.trim();
    if (!value) return null;

    if (ID_RE.test(value)) return { id: value };

    let id: string | null = null;
    for (const pattern of HOST_PATTERNS) {
        const match = value.match(pattern);
        if (match) {
            id = match[1];
            break;
        }
    }
    if (!id) return null;

    let params: URLSearchParams;
    try {
        const url = new URL(value.startsWith("http") ? value : `https://${value}`);
        params = new URLSearchParams(url.search);
        if (url.hash.startsWith("#t=")) {
            params.set("t", url.hash.slice(3));
        }
    } catch {
        return { id };
    }

    const start = parseTimestamp(params.get("start") ?? params.get("t"));
    const end = parseTimestamp(params.get("end"));

    const video: SignVideo = { id };
    if (start !== undefined) video.start = start;
    if (end !== undefined && (start === undefined || end > start)) video.end = end;
    return video;
}

export function youTubeThumbnail(video: SignVideo): string {
    return `https://img.youtube.com/vi/${video.id}/hqdefault.jpg`;
}

export function youTubeEmbedUrl(video: SignVideo, autoplay: boolean): string {
    const params = new URLSearchParams({
        rel: "0",
        modestbranding: "1",
        playsinline: "1",
    });
    if (autoplay) params.set("autoplay", "1");
    if (video.start !== undefined) params.set("start", String(video.start));
    if (video.end !== undefined) params.set("end", String(video.end));
    return `https://www.youtube-nocookie.com/embed/${video.id}?${params.toString()}`;
}
